import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useCollectiblesStore } from 'src/store/collectibles/collectibles.store';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';

const STATUS_OPTIONS = ['active', 'inactive'];
const STATUS_LABEL = ['Ativo', 'Inativo'];

const PRIVATE_LABEL = ['Privado', 'Público'];
const PRIVATE_OPTIONS = [true, false];

const DONT_LABEL = ['Importados', 'Não importados'];
const DONT_OPTIONS = [true, false];

const COLLECTIBLE_TYPE_ERROR_MESSAGES: Record<string, string> = {
  invalid_collectible_type: 'Tipo de colecionável inválido.',
  collectible_not_found: 'Colecionável não encontrado.',
  collection_not_found: 'Coleção não encontrada.',
  insufficient_free_collectibles_to_unlock:
    'A coleção precisa manter colecionáveis livres e ativos suficientes para o desbloqueio.',
};

type CollectionUnavailableDateRange = {
  startDate: Date;
  endDate: Date;
};

const normalizeCollectionDate = (date: Date | string | null | undefined) => {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
};

const isDateInsideRange = (date: Date, range: CollectionUnavailableDateRange) =>
  date >= range.startDate && date <= range.endDate;

const doesDateRangeOverlap = (
  startDate: Date,
  endDate: Date,
  range: CollectionUnavailableDateRange
) => startDate <= range.endDate && endDate >= range.startDate;

export default function useCollectionsDetails() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();

  const [
    isLoadingCollectionDetails,
    isLoadingCollectionPeriods,
    isLoadingCollectibles,
    updatingCollectibleTypeId,
    collectiblesList,
    collectionDetails,
    collectionPeriods,
    getCollectionDetails,
    getCollectionPeriods,
    getCollectiblesList,
    resetCollectionDetails,
    createCollection,
    updateCollection,
    updateCollectibleType,
    resetCollectiblesList,
  ] = useCollectiblesStore((state) => [
    state.isLoadingCollectionDetails,
    state.isLoadingCollectionPeriods,
    state.isLoadingCollectibles,
    state.updatingCollectibleTypeId,
    state.collectiblesList,
    state.collectionDetails,
    state.collectionPeriods,
    state.getCollectionDetails,
    state.getCollectionPeriods,
    state.getCollectiblesList,
    state.resetCollectionDetails,
    state.createCollection,
    state.updateCollection,
    state.updateCollectibleType,
    state.resetCollectiblesList,
  ]);

  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const unavailableCollectionDateRanges = useMemo(
    () =>
      collectionPeriods
        .filter((period) => !isEdit || String(period._id) !== String(id))
        .map((period) => {
          const startDate = normalizeCollectionDate(period.startDate);
          const endDate = normalizeCollectionDate(period.endDate);

          if (!startDate || !endDate) {
            return null;
          }

          return startDate <= endDate
            ? { startDate, endDate }
            : { startDate: endDate, endDate: startDate };
        })
        .filter((period): period is CollectionUnavailableDateRange => Boolean(period)),
    [collectionPeriods, id, isEdit]
  );
  const activeFreeCollectiblesCount = useMemo(
    () =>
      collectiblesList.filter(
        (collectible) => collectible.status === 'active' && collectible.type !== 'blocked'
      ).length,
    [collectiblesList]
  );
  const [openCpfs, setOpenCpfs] = useState<boolean>(false);
  const [fileNameCpfs, setFileNameCpfs] = useState<string>('');
  const [cpfs, setCpfs] = useState<any[]>([]);
  const [hasLoadedCollectibles, setHasLoadedCollectibles] = useState(false);

  useEffect(() => {
    getCollectionPeriods();

    if (id) {
      getCollectionDetails(id);
      getCollectiblesList(id).then(
        () => setHasLoadedCollectibles(true),
        () => setHasLoadedCollectibles(true)
      );
    } else {
      resetCollectionDetails();
      resetCollectiblesList();
    }

    return () => {
      resetCollectionDetails();
      resetCollectiblesList();
    };
  }, []);

  const defaultValues = useMemo(
    () => ({
      name: collectionDetails?.name || '',
      title: collectionDetails?.title || '',
      image: collectionDetails?.image || '',
      startDate: collectionDetails?.startDate ? new Date(collectionDetails.startDate) : new Date(),
      endDate: collectionDetails?.endDate ? new Date(collectionDetails.endDate) : new Date(),
      rules: collectionDetails?.rules || '',
      private: collectionDetails?.private || false,
      dontShowToImported: collectionDetails?.dontShowToImported || false,
      status: (collectionDetails?.status as 'active' | 'inactive') || 'active',
      freeRedemptionsRequiredToUnlock: collectionDetails?.freeRedemptionsRequiredToUnlock ?? 0,
    }),
    [collectionDetails]
  );

  useEffect(() => {
    if (isEdit && id) {
      setCpfs(collectionDetails?.cpfs || []);
    }
  }, [collectionDetails, isEdit]);

  const isCollectionDateUnavailable = useCallback(
    (date: Date | null | undefined) => {
      const normalizedDate = normalizeCollectionDate(date);

      if (!normalizedDate) {
        return false;
      }

      return unavailableCollectionDateRanges.some((range) =>
        isDateInsideRange(normalizedDate, range)
      );
    },
    [unavailableCollectionDateRanges]
  );

  const doesCollectionRangeOverlapUnavailable = useCallback(
    (startDate: Date | null | undefined, endDate: Date | null | undefined) => {
      const normalizedStartDate = normalizeCollectionDate(startDate);
      const normalizedEndDate = normalizeCollectionDate(endDate);

      if (!normalizedStartDate || !normalizedEndDate || normalizedStartDate > normalizedEndDate) {
        return false;
      }

      return unavailableCollectionDateRanges.some((range) =>
        doesDateRangeOverlap(normalizedStartDate, normalizedEndDate, range)
      );
    },
    [unavailableCollectionDateRanges]
  );

  const collectionSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    title: Yup.string().notRequired(),
    image: Yup.mixed<File | string>().test(
      'required',
      'Campo obrigatório!',
      (value) => value instanceof File || (typeof value === 'string' && value.length > 0)
    ),
    startDate: Yup.date()
      .required('Campo obrigatório!')
      .test(
        'not-in-unavailable-collection',
        'Esta data já está ocupada por outra coleção',
        (value) => !isCollectionDateUnavailable(value)
      ),
    endDate: Yup.date()
      .required('Campo obrigatório!')
      .test(
        'is-after-start',
        'A data de término deve ser posterior à data de início',
        function isAfterStart(value) {
          const { startDate } = this.parent;
          return !value || !startDate || new Date(value) > new Date(startDate);
        }
      )
      .test(
        'not-in-unavailable-collection',
        'Esta data já está ocupada por outra coleção',
        (value) => !isCollectionDateUnavailable(value)
      )
      .test(
        'range-does-not-overlap-unavailable-collection',
        'O período selecionado cruza uma coleção existente',
        function validateUnavailableCollectionRange(value) {
          const { startDate } = this.parent;

          return !doesCollectionRangeOverlapUnavailable(startDate, value);
        }
      ),
    rules: Yup.string().required('Campo obrigatório!'),
    private: Yup.boolean(),
    dontShowToImported: Yup.boolean(),
    status: Yup.mixed<'active' | 'inactive'>()
      .oneOf(['active', 'inactive'])
      .required('Campo obrigatório!'),
    freeRedemptionsRequiredToUnlock: isEdit
      ? Yup.number()
          .typeError('Informe um número válido')
          .integer('Informe um número inteiro')
          .min(0, 'O valor mínimo é 0')
          .max(
            activeFreeCollectiblesCount,
            activeFreeCollectiblesCount === 0
              ? 'O valor deve ser 0'
              : `O valor máximo é ${activeFreeCollectiblesCount}`
          )
          .required('Campo obrigatório!')
      : Yup.number().notRequired(),
  });

  const methods = useForm({
    resolver: yupResolver(collectionSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    trigger,
    control,
    formState: { isSubmitting },
  } = methods;

  const values = watch();
  const start = watch('startDate');
  const end = watch('endDate');
  const isPrivateRaw = watch('private');
  const isPrivate = useMemo(() => String(isPrivateRaw) === 'true', [isPrivateRaw]);

  const hasUnavailableCollectionDateRangeOverlap = useMemo(
    () => doesCollectionRangeOverlapUnavailable(start, end),
    [doesCollectionRangeOverlapUnavailable, end, start]
  );

  const shouldDisableCollectionDate = useCallback(
    (date: Date, field: 'startDate' | 'endDate') => {
      const normalizedDate = normalizeCollectionDate(date);

      if (!normalizedDate) {
        return false;
      }

      if (isCollectionDateUnavailable(normalizedDate)) {
        return true;
      }

      const normalizedStartDate =
        field === 'startDate' ? normalizedDate : normalizeCollectionDate(start);
      const normalizedEndDate = field === 'endDate' ? normalizedDate : normalizeCollectionDate(end);

      if (!normalizedStartDate || !normalizedEndDate || normalizedStartDate > normalizedEndDate) {
        return false;
      }

      return doesCollectionRangeOverlapUnavailable(normalizedStartDate, normalizedEndDate);
    },
    [doesCollectionRangeOverlapUnavailable, end, isCollectionDateUnavailable, start]
  );

  const isValidEndDate = useMemo(() => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return endDate.getTime() >= startDate.getTime();
  }, [end, start]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [collectionDetails, reset, defaultValues]);

  useEffect(() => {
    if (!isEdit || !collectionDetails || isLoadingCollectibles || !hasLoadedCollectibles) {
      return;
    }

    const fieldName = 'freeRedemptionsRequiredToUnlock';
    const currentValue = Number(getValues(fieldName));
    let nextValue = currentValue;

    if (activeFreeCollectiblesCount === 0) {
      nextValue = 0;
    }

    if (currentValue !== nextValue) {
      setValue(fieldName, nextValue, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    trigger(fieldName);
  }, [
    activeFreeCollectiblesCount,
    collectionDetails,
    getValues,
    hasLoadedCollectibles,
    isEdit,
    isLoadingCollectibles,
    setValue,
    trigger,
  ]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const updatedData = {
        ...data,
        cpfs: isPrivate ? cpfs || [] : [],
      };
      if (isEdit && id) {
        await updateCollection(id, updatedData);
        enqueueSnackbar('Coleção atualizada com sucesso!');
      } else {
        const createData = { ...updatedData };
        delete createData.freeRedemptionsRequiredToUnlock;
        const response = await createCollection(createData);
        if (response) {
          enqueueSnackbar('Coleção criada com sucesso!');
          navigate(paths.dashboard.collectionsDetails(response._id));
        }
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const handleToggleCollectibleType = useCallback(
    async (collectibleId: string, currentType: 'free' | 'blocked') => {
      const nextType = currentType === 'blocked' ? 'free' : 'blocked';

      try {
        await updateCollectibleType(collectibleId, nextType);
        enqueueSnackbar(
          nextType === 'blocked'
            ? 'Colecionável bloqueado com sucesso!'
            : 'Colecionável liberado com sucesso!',
          { variant: 'success' }
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '';
        enqueueSnackbar(
          COLLECTIBLE_TYPE_ERROR_MESSAGES[errorMessage] ||
            errorMessage ||
            'Não foi possível atualizar o tipo do colecionável.',
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar, updateCollectibleType]
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'image',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleUploadCpfs = (e: any) => {
    e.preventDefault();
    const { files } = e.target;
    // eslint-disable-next-line
    var f = files[0];

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary' });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (dataParse?.[0]?.[0] !== 'Cpfs abaixo') {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      const updatedDataParse = dataParse?.slice(1).map((row: any[]) => row[0]);
      setCpfs(updatedDataParse);
      setFileNameCpfs(f.name);
      setCpfs(updatedDataParse);
      enqueueSnackbar('Códigos adicionados com Sucesso!');
    };
    reader.readAsBinaryString(f);
  };

  const handleDownloadCpfs = useCallback(() => {
    const cpfsToDownload = cpfs
      .map((cpf) => (Array.isArray(cpf) ? cpf?.[0] : cpf))
      .filter(Boolean)
      .map((cpf) => String(cpf));

    if (!cpfsToDownload.length) {
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Cpfs abaixo'],
      ...cpfsToDownload.map((cpf) => [cpf]),
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cpfs');

    XLSX.writeFile(workbook, `cpfs_beneficio_${id || new Date().getTime()}.xlsx`);
  }, [id, cpfs]);

  const onCloseCpfs = () => {
    setOpenCpfs(false);
  };

  const hasCpfsToDownload = useMemo(() => isEdit && cpfs && cpfs.length > 0, [isEdit, cpfs]);

  return {
    isEdit,
    isLoadingCollectionDetails,
    isLoadingCollectionPeriods,
    methods,
    values,
    isSubmitting,
    STATUS_OPTIONS,
    STATUS_LABEL,
    PRIVATE_OPTIONS,
    PRIVATE_LABEL,
    control,
    isValidEndDate,
    hasUnavailableCollectionDateRangeOverlap,
    shouldDisableCollectionDate,
    id,
    isLoadingCollectibles,
    updatingCollectibleTypeId,
    collectiblesList,
    activeFreeCollectiblesCount,
    isPrivate,
    DONT_LABEL,
    DONT_OPTIONS,
    cpfs,
    openCpfs,
    fileNameCpfs,
    hasCpfsToDownload,
    handleDownloadCpfs,
    handleDrop,
    handleToggleCollectibleType,
    onSubmit,
    navigate,
    setOpenCpfs,
    handleUploadCpfs,
    onCloseCpfs,
  };
}
